import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { SEO } from 'components/SEO';
import { graphql } from 'gatsby';
import React, { lazy, LazyExoticComponent, useEffect } from 'react';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
import { IContentWithImage } from 'utils/types';

const FullBackgroundSection = lazy(
  () => import('components/FullBackgroundSection'),
);
const MembersSatisfaction = lazy(
  () => import('containers/HealthPlansPage/MembersSatisfaction'),
);
const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);

interface HeroProps {
  eyebrow: string;
  eyebrowColor: string;
  headline: string;
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  backgroundColor: string;
  desktopImage: {
    url: string;
    title: string;
  };
  cta: {
    text: string;
    url: string;
  };
}

interface SectionProps {
  sectionImage: {
    url: string;
  };
  componentName: string;
  headline: string;
  disclaimer?: {
    disclaimer: string;
  };
  description: {
    description: string;
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  slug: string;
  title?: string;
  listOfContentWithImages?: IContentWithImage[];
  container?: any;
}

type Props = {
  data: {
    contentfulPage001: {
      slug: string;
      hero: HeroProps;
      sections: SectionProps[];
    };
  };
};

const components: {
  HeroSection: React.FC<any>;
  MembersSatisfaction: LazyExoticComponent<React.FC<any>>;
  FullBackgroundSection: LazyExoticComponent<React.FC<any>>;
  HowItWorksSection: LazyExoticComponent<React.FC<any>>;
} = {
  HeroSection,
  MembersSatisfaction,
  FullBackgroundSection,
  HowItWorksSection,
};

export const Head = () => <SEO />;

const ProvidersPage: React.FC<Props> = ({ data }) => {
  const { hero, sections } = data.contentfulPage001;

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Providers page',
      },
    });
  }, []);

  return (
    <Layout>
      <HeroSection {...hero} />

      {sections.map(
        (
          section: Props['data']['contentfulPage001']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          return (
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
};

export default ProvidersPage;

export const data = graphql`
  query ProvidersPageQuery {
    contentfulPage001(displayName: { eq: "Providers Page | EHS" }) {
      slug
      hero {
        ... on ContentfulHeroSection {
          eyebrow
          eyebrowColor
          headline
          description {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          backgroundColor
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        headline
        title
        subHeadline
        componentName
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          gatsbyImageData(placeholder: BLURRED, quality: 75)
          url
        }
        cta {
          appearance
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            backgroundColor
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
                html
              }
            }
            image {
              title
              url
            }
            cta {
              text
              url
            }
          }
        }
      }
    }
  }
`;
